import * as Sentry from '@sentry/react'
import { skipToken } from '@reduxjs/toolkit/query'
import { type FC, type PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { getSupportedProviderFromAdhocMeetingLink, PROVIDER_CONFIG } from '../../../common/meetingProviders'
import { Button } from '../../../components/Button/Button'
import { SimpleConsentIcon } from '../../../components/Consent/ConsentIcon'
import { UserConsentState } from '../../../components/Consent/model'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { BookingStatus, MeetingMode, type Booking, type BookingInvitee, type Location } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useGetUserByIdQuery } from '../../../store/user/user'
import { addToast } from '../../../store/toast/toastSlice'
import { formatRelativeDay, formatSentDate, getBookingLocationLine, getMeetingIdFromUrl } from '../bookingFormat'
import { clientDisplayName } from '../shared/schedulingDisplay'

const Section: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <div className='d-flex flex-column zocks-gap-0.5'>
    <div className='paragraph-small bold zocks-text-gray-600 text-uppercase'>{label}</div>
    <div className='d-flex flex-column paragraph-small zocks-gap-0.125'>{children}</div>
  </div>
)

const inviteeLabel = (invitee: BookingInvitee): string => (invitee.userId ? 'Team member' : (invitee.role ?? 'Guest'))

// Participants who are Zocks contacts link to their contact page; everyone else is plain text.
const ContactName: FC<{ contactId?: string; name: string }> = ({ contactId, name }) =>
  contactId ? (
    <Link to={`/library/manage-contacts/contacts/${contactId}`} className='text-decoration-underline zocks-text-gray-900'>
      {name}
    </Link>
  ) : (
    <>{name}</>
  )

interface BookingLinkRowProps {
  url: string
  clientName?: string
}

const BookingLinkRow: FC<BookingLinkRowProps> = ({ url, clientName }) => {
  const dispatch = useAppDispatch()
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy the link', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Booking link copied', messages: ['Paste it anywhere to share with the client.'] }))
  }
  return (
    <div className='d-flex align-items-center zocks-gap-0.5 border rounded-3 zocks-border-color-gray-300 zocks-bg-color-white px-3 py-3'>
      <i className='bi bi-link-45deg zocks-text-gray-600' aria-hidden='true' />
      {clientName != null ? <span className='text-nowrap'>{clientName} -</span> : null}
      <span className='text-truncate zocks-text-gray-700 flex-grow-1'>{url.replace(/^https?:\/\//, '')}</span>
      <Button variant='primaryLinkButton' className='p-0 text-decoration-none flex-shrink-0' onClick={handleCopy}>
        Copy <i className='bi bi-copy' aria-hidden='true' />
      </Button>
    </div>
  )
}

const NoteBox: FC<{ author?: string; message: string }> = ({ author, message }) => (
  <div className='d-flex align-items-start zocks-gap-0.5 border rounded-2 zocks-border-color-gray-300 zocks-bg-color-white px-3 py-2'>
    <i className='bi bi-chat-left-text zocks-text-gray-600' aria-hidden='true' />
    <div className='d-flex flex-column zocks-gap-0.25'>
      {author ? <div className='zocks-text-gray-600'>{author}</div> : null}
      <div>{message}</div>
    </div>
  </div>
)

interface BookingDetailsProps {
  booking: Booking
  location?: Location
  variant?: 'live' | 'history'
  cancellationAuthor?: string
}

export const BookingDetails: FC<BookingDetailsProps> = ({ booking, location, variant = 'live', cancellationAuthor }) => {
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const isOwnerSelf = booking.userId === authenticatedUser.id
  const ownerQuery = useGetUserByIdQuery(isOwnerSelf ? skipToken : { accountId: authenticatedUser.accountId, userId: booking.userId })
  const ownerName = getUserDisplayName(isOwnerSelf ? authenticatedUser : ownerQuery.data?.data)
  const invitees = booking.additionalInvitees ?? []
  const isHistory = variant === 'history'
  const isInPerson = booking.meetingMode === MeetingMode.IN_PERSON
  const hasJoinLink = !isHistory && !isInPerson && booking.meetingUrl != null && typeof booking.meetingUrl === 'string' && booking.meetingUrl.length > 0
  const provider = booking.meetingUrl ? getSupportedProviderFromAdhocMeetingLink(booking.meetingUrl) : undefined
  const providerConfig = provider ? PROVIDER_CONFIG[provider] : undefined
  const meetingId = booking.meetingUrl ? getMeetingIdFromUrl(booking.meetingUrl) : undefined
  const whereLine = isInPerson
    ? getBookingLocationLine(booking, location)
    : isHistory
      ? undefined
      : booking.status === BookingStatus.REQUESTED
        ? 'Join link will be generated once you accept this request.'
        : booking.status === BookingStatus.CONFIRMED
          ? 'No join link was added. Add one on the event in your scheduling calendar, then send the update when your calendar asks.'
          : undefined
  const noteAuthor = booking.bookedBy ? clientDisplayName(booking.bookedBy) : undefined
  const rescheduleNoteAuthor = noteAuthor ? `${noteAuthor} · Rescheduling note` : 'Rescheduling note'
  const cancellationMessage = isHistory ? booking.cancellationReason : undefined
  const isAwaitingLive = !isHistory && booking.status === BookingStatus.AWAITING_REPLY
  const clientLinks = isAwaitingLive
    ? booking.clients.flatMap((client) => (client.inviteUrl != null && client.inviteUrl !== '' ? [{ client, url: client.inviteUrl }] : []))
    : []
  const sentCount = booking.reminders?.count ?? 0
  const totalCount = booking.reminders?.total ?? 0
  const nextSendAt = booking.reminders?.nextSendAt
  const nextReminderLine = nextSendAt != null ? `${formatRelativeDay(new Date(nextSendAt))} · ${formatSentDate(new Date(nextSendAt))}` : null
  const showReminders = isAwaitingLive && totalCount > 0

  return (
    <div className='d-flex align-items-start zocks-gap-2 p-3 border border-top-0 rounded-bottom zocks-border-color-gray-200 zocks-bg-color-gray-100'>
      <div className='d-flex flex-column zocks-gap-1.5 flex-grow-1'>
        {booking.clients.length > 0 ? (
          <Section label='Clients'>
            {booking.clients.map((client, index) => (
              <div key={client.contactId ?? client.email ?? index} className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
                <span>
                  <ContactName contactId={client.contactId} name={clientDisplayName(client)} />{' '}
                  {!client.contactId ? <span className='zocks-text-gray-600'>(Guest)</span> : null}
                </span>
                {client.consent?.status === 'accepted' ? (
                  <span title={client.consent.text || 'Consent given on the booking page'}>
                    <SimpleConsentIcon consentState={UserConsentState.ACCEPTED} role='img' />
                  </span>
                ) : null}
              </div>
            ))}
          </Section>
        ) : null}
        {ownerName ? (
          <Section label='Owner'>
            <div>
              {ownerName} {isOwnerSelf ? <span className='zocks-text-gray-600'>(Me)</span> : null}
            </div>
          </Section>
        ) : null}
        {invitees.length > 0 ? (
          <Section label='Also invited'>
            {invitees.map((invitee, index) => (
              <div key={invitee.userId ?? invitee.contactId ?? invitee.email ?? index}>
                <ContactName contactId={invitee.contactId} name={clientDisplayName(invitee)} />{' '}
                <span className='zocks-text-gray-600'>({inviteeLabel(invitee)})</span>
              </div>
            ))}
          </Section>
        ) : null}
      </div>
      <div className='d-flex flex-column zocks-gap-1.5 w-50'>
        {clientLinks.length > 0 ? (
          <Section label='Booking link'>
            {clientLinks.map(({ client, url }) => (
              <BookingLinkRow
                key={client.contactId ?? client.email ?? url}
                url={url}
                clientName={clientLinks.length > 1 ? clientDisplayName(client) : undefined}
              />
            ))}
          </Section>
        ) : null}
        {showReminders ? (
          <Section label='Reminders'>
            <div className='d-flex zocks-gap-3'>
              <div className='d-flex flex-column zocks-gap-0.125 zocks-text-gray-600'>
                <div>Sent</div>
                {nextReminderLine != null ? <div>Next</div> : null}
              </div>
              <div className='d-flex flex-column zocks-gap-0.125'>
                <div>
                  {sentCount} of {totalCount}
                </div>
                {nextReminderLine != null ? <div>{nextReminderLine}</div> : null}
              </div>
            </div>
          </Section>
        ) : null}
        {hasJoinLink ? (
          <Section label='Where'>
            <div className='d-flex align-items-center zocks-gap-0.5'>
              {providerConfig ? (
                <IntegrationIcon serviceId={providerConfig.serviceId} width={16} height={16} />
              ) : (
                <i className='bi bi-camera-video zocks-text-gray-600' aria-hidden='true' />
              )}
              <a href={booking.meetingUrl} target='_blank' rel='noopener noreferrer' className='bold zocks-text-primary text-decoration-none'>
                Join link
              </a>
              {meetingId ? (
                <>
                  <span className='zocks-text-gray-600'>·</span>
                  <span className='zocks-text-gray-700'>ID {meetingId}</span>
                </>
              ) : null}
            </div>
          </Section>
        ) : whereLine ? (
          <Section label='Where'>
            <div className='d-flex align-items-center zocks-gap-0.5 zocks-text-gray-700'>
              {isInPerson ? <i className='bi bi-geo-alt' aria-hidden='true' /> : null}
              {whereLine}
            </div>
          </Section>
        ) : null}
        {booking.clientMessage || booking.rescheduleMessage || cancellationMessage ? (
          <Section label='Notes'>
            {booking.clientMessage ? <NoteBox author={noteAuthor} message={booking.clientMessage} /> : null}
            {booking.rescheduleMessage ? <NoteBox author={rescheduleNoteAuthor} message={booking.rescheduleMessage} /> : null}
            {cancellationMessage ? <NoteBox author={cancellationAuthor} message={cancellationMessage} /> : null}
          </Section>
        ) : null}
      </div>
    </div>
  )
}
